import React, { createContext, useState, useEffect, ReactNode } from "react";
import { message } from "antd";
import i18next from "i18next";
import { apiUrl } from "./api";
// Define the structure for the context data
export interface AuthContextType {
  token: string | null;
  role: string | null;
  login: (token: string) => void;
  logout: (sessionEnded?:boolean) => void;
  setRole: (role: string) => void;
  userName:string|null;
  setName: (name:string)=>void;
  notification:boolean;
  setNotification: (status:boolean)=>void;
  email:any;
  setemail:any;
  trustedDeviceToken: string | null;
  setTrustedDeviceTokenState: (token: string) => void;
  capabilities: string[];
  capabilitiesLoaded: boolean;
  hasCapability: (name: string, fallbackRoles?: string | string[]) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState(  sessionStorage.getItem("accessToken"));
  const [role, setRoleState] = useState(sessionStorage.getItem("role"));
  const [userName, setUsername] = useState(sessionStorage.getItem("userName"));
  const [email, setEmail] = useState(sessionStorage.getItem("email"));
  const [trustedDeviceToken, setTrustedDeviceToken] = useState(
    sessionStorage.getItem("trustedDeviceToken")
  );

  const setTrustedDeviceTokenState = (token: string) => {
    setTrustedDeviceToken(token);
    sessionStorage.setItem("trustedDeviceToken", token);
  };

  const [notification,setNotification]= useState(false)

  const [capabilities, setCapabilities] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem("capabilities") || "[]");
    } catch {
      return [];
    }
  });
  // True once a capabilities fetch has resolved (success) for the current session,
  // or immediately if a prior fetch's result is already cached from this same session.
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState<boolean>(
    () => !!sessionStorage.getItem("capabilities")
  );

  const fetchCapabilities = async (authToken: string) => {
    try {
      const res = await fetch(`${apiUrl}/auth/capabilities`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        const permissions = data.permissions || [];
        setCapabilities(permissions);
        sessionStorage.setItem("capabilities", JSON.stringify(permissions));
      }
    } catch {
      // capability gating is UI-only; a failed fetch just leaves buttons hidden, not fatal
    } finally {
      setCapabilitiesLoaded(true);
    }
  };

  useEffect(() => {
    if (token) fetchCapabilities(token);
  }, []);

  // Until the capabilities fetch has resolved, gated UI falls back to a legacy role check
  // (if the caller provides one) instead of defaulting to hidden — avoids a flash of missing
  // actions for legitimate users during the brief async window right after login/page load.
  const hasCapability = (name: string, fallbackRoles?: string | string[]) => {
    if (!capabilitiesLoaded) {
      if (fallbackRoles == null) return false;
      const allowed = Array.isArray(fallbackRoles) ? fallbackRoles : [fallbackRoles];
      return role != null && allowed.includes(role);
    }
    return capabilities.includes(name);
  };

  // const [token, setToken] = useState<string | null>(null);
  // const [role, setRoleState] = useState<string | null>(null);

  const setRole = (role: string) => {
    setRoleState(role);
    sessionStorage.setItem("role", role);
  };



  const setName = (name: string) => {
    setUsername(name);
    sessionStorage.setItem("userName", name);
  };

  const setemail = (email: string) => {
    setEmail(email);
    sessionStorage.setItem("email", email);
  };

  const login = (token: string) => {
    setToken(token);
    sessionStorage.setItem("accessToken", token);
    fetchCapabilities(token);
  };

  const logout = (sessionEnded?:boolean) => {
    message.destroy();
    if(sessionEnded){message.error(i18next.t("Session Ended, please login again"))}
    setToken(null);
    setRoleState(null);
    setCapabilities([]);
    setCapabilitiesLoaded(false);
    // localStorage.clear();

    sessionStorage.clear();
  };

  return (
    <AuthContext.Provider value={{ token, role, login, logout, setRole,setName,userName,notification,setNotification,email,setemail,trustedDeviceToken,setTrustedDeviceTokenState,capabilities,capabilitiesLoaded,hasCapability }}>
      {children}
    </AuthContext.Provider>
  );
};
